import { httpGet } from 'utils';
import { ToolExecutionResult } from '../types';
import { formatCurrency, formatPercentage } from '../utils/formatters';

interface PortalHolding {
  lwin18: string;
  wineName: string;
  vintage: number;
  region: string;
  cultWinesAllocationRegion: string;
  unit: string;
  qty: number;
  qtyOwned: number;
  totalValue: number;
  totalCost: number;
  pnl: number;
  pnlPercent: number;
  marketPrice: number;
  marketPriceDate: string;
  avgCostPerUnit: number;
  location: string;
  // Price history
  price1MonthAgo: number;
  price3MonthsAgo: number;
  price6MonthsAgo: number;
  price12MonthsAgo: number;
  changePercent1Month: number;
  changePercent3Months: number;
  changePercent6Months: number;
  changePercent12Months: number;
  // Ratings
  wa: number | null;
  vinous: string | null;
  // Trading
  highestBidPrice: number | null;
  bidsCount: number | null;
  cwLastTradePrice: number | null;
  lastTradeDateTime: string | null;
  highestBid: {
    lwin18: string;
    price: number;
    quantityOutstanding: number;
    orderType: string;
  } | null;
  lowestOffer: {
    lwin18: string;
    price: number;
    quantityOutstanding: number;
    orderType: string;
  } | null;
  // Other
  autoTradeSetting: string;
  totalBtls: number;
  forSale: number;
  qtyForSale: number;
  // Quality Metrics
  grade: number | null;
  fairValueScore: number | null;
  liquidityScore: number | null;
  performanceScore: number | null;
  vintageScore: number | null;
  // Normalized critic score (combined WA/Vinous score)
  normalizedCriticScore: number | null;
}

interface PortalCurrentHoldingsResponse {
  data: PortalHolding[];
  count: number;
}

const fetchPortalCurrentHoldings = (portfolioId: number) =>
  httpGet<PortalCurrentHoldingsResponse, { portfolioId: number }>('portal/PortalCurrentHoldings', { portfolioId });

export const executeGetPortalCurrentHoldings = async (args: Record<string, unknown>): Promise<ToolExecutionResult> => {
  try {
    const portfolioId = (args.portfolioId as number) ?? -1; // -1 for all portfolios
    const sortBy = (args.sortBy as string) || 'totalValue';
    const sortDirection = (args.sortDirection as 'asc' | 'desc') || 'desc';
    const limit = (args.limit as number) || 10;
    const includeMarketData = args.includeMarketData !== false; // default true

    const response = await fetchPortalCurrentHoldings(portfolioId);

    if (!response.data || response.data.length === 0) {
      return {
        success: true,
        data: {
          message: 'No holdings found',
          holdings: [],
        },
      };
    }

    // Sort the data
    const holdings = [...response.data];
    holdings.sort((a, b) => {
      let aVal: number | string;
      let bVal: number | string;

      switch (sortBy) {
        case 'wineName':
          aVal = a.wineName;
          bVal = b.wineName;
          break;
        case 'vintage':
          aVal = a.vintage || 0;
          bVal = b.vintage || 0;
          break;
        case 'pnl':
        case 'profitAndLoss':
          aVal = a.pnl;
          bVal = b.pnl;
          break;
        case 'pnlPercent':
        case 'profitAndLossPct':
          aVal = a.pnlPercent;
          bVal = b.pnlPercent;
          break;
        case 'marketPrice':
          aVal = a.marketPrice || 0;
          bVal = b.marketPrice || 0;
          break;
        case 'changePercent1Month':
          aVal = a.changePercent1Month || 0;
          bVal = b.changePercent1Month || 0;
          break;
        case 'changePercent12Months':
          aVal = a.changePercent12Months || 0;
          bVal = b.changePercent12Months || 0;
          break;
        case 'totalValue':
        default:
          aVal = a.totalValue;
          bVal = b.totalValue;
          break;
      }

      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortDirection === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
      }

      return sortDirection === 'asc' ? (aVal as number) - (bVal as number) : (bVal as number) - (aVal as number);
    });

    // Limit results
    const limitedHoldings = holdings.slice(0, limit);

    // Calculate portfolio totals from all holdings
    const totalValue = response.data.reduce((sum, h) => sum + (h.totalValue || 0), 0);
    const totalCost = response.data.reduce((sum, h) => sum + (h.totalCost || 0), 0);
    const totalPnL = response.data.reduce((sum, h) => sum + (h.pnl || 0), 0);
    const totalUnits = response.data.reduce((sum, h) => sum + (h.qty || 0), 0);
    const totalBottles = response.data.reduce((sum, h) => sum + (h.totalBtls || 0), 0);

    // Format holdings for LLM consumption
    const formattedHoldings = limitedHoldings.map((h, index) => {
      const base = {
        rank: index + 1,
        wine: `${h.wineName} ${h.vintage}`,
        region: h.cultWinesAllocationRegion || h.region,
        unit: h.unit,
        quantity: h.qty,
        bottles: h.totalBtls,
        location: h.location,
        totalValue: formatCurrency(h.totalValue),
        totalCost: formatCurrency(h.totalCost),
        profitAndLoss: formatCurrency(h.pnl),
        profitAndLossPct: formatPercentage(h.pnlPercent * 100),
        avgCostPerUnit: formatCurrency(h.avgCostPerUnit),
      };

      if (includeMarketData) {
        return {
          ...base,
          marketPrice: formatCurrency(h.marketPrice),
          marketPriceDate: h.marketPriceDate?.split('T')[0] || null,
          priceChanges: {
            oneMonth: formatPercentage(h.changePercent1Month),
            threeMonths: formatPercentage(h.changePercent3Months),
            sixMonths: formatPercentage(h.changePercent6Months),
            twelveMonths: formatPercentage(h.changePercent12Months),
          },
          ratings: {
            wineAdvocate: h.wa,
            vinous: h.vinous,
            criticScore: h.normalizedCriticScore,
          },
          qualityMetrics: {
            grade: h.grade,
            fairValue: h.fairValueScore,
            liquidity: h.liquidityScore,
            performance: h.performanceScore,
            vintage: h.vintageScore,
          },
          trading: {
            highestBid: h.highestBid ? formatCurrency(h.highestBid.price) : null,
            lowestOffer: h.lowestOffer ? formatCurrency(h.lowestOffer.price) : null,
            lastTradePrice: h.cwLastTradePrice ? formatCurrency(h.cwLastTradePrice) : null,
            forSale: h.qtyForSale,
          },
          autoTradeSetting: h.autoTradeSetting,
        };
      }

      return base;
    });

    return {
      success: true,
      data: {
        holdings: formattedHoldings,
        sortedBy: sortBy,
        sortDirection,
        showing: limitedHoldings.length,
        totalHoldings: response.count || response.data.length,
        portfolioSummary: {
          totalValue: formatCurrency(totalValue),
          totalCost: formatCurrency(totalCost),
          totalProfitAndLoss: formatCurrency(totalPnL),
          totalProfitAndLossPct: totalCost > 0 ? formatPercentage((totalPnL / totalCost) * 100) : '0%',
          totalUnits,
          totalBottles,
          uniqueWines: response.data.length,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error: `Failed to fetch holdings: ${(error as Error).message}`,
    };
  }
};
